<template>
  <v-row dense justify="center">
    <v-dialog
      v-model="dialog"
      scrollable
      persistent
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-card-title>
          <span class="headline">Task Creation</span>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row class="mb-0 mt-0 pb-0 pt-0">
              <v-col cols="7" class="mb-0 mt-0 pb-0 pt-0">
                <v-row>
                  <v-col class="mb-0 mt-0 pb-0 pt-0">
                    <v-text-field
                      v-model="name"
                      :counter="120"
                      :rules="nameRules"
                      label="Task name*"
                      required
                    />
                  </v-col>
                </v-row>
                <PracticeSearch
                  @onBusinessChanged="handleBusinessChange"
                  @onEnvChanged="handleEnvChanged"
                />
                <h3>Input files</h3>
                <v-divider horizontal></v-divider>

                <v-row>
                  <v-col cols="5.5">
                    <UploadsSelect
                      label="Select existing file"
                      :disabled="
                        sessionsLoading ||
                        sessionInventoryFileUsage ||
                        sessionSettingsChecking
                      "
                      :uploads="uploads"
                      :selected="selectedInputFiles"
                      multiple
                      @onSelect="(selected) => (selectedInputFiles = selected)"
                    />
                  </v-col>
                  <v-col cols="1" class="pt-10" align="center">
                    <h3>or</h3>
                  </v-col>
                  <v-col cols="5.5">
                    <v-file-input
                      accept=".csv, .xls, .xlsx"
                      :disabled="sessionInventoryFileUsage"
                      :loading="sessionsLoading || sessionSettingsChecking"
                      show-size
                      counter
                      multiple
                      v-model="inputFiles"
                      label="Upload new"
                    />
                  </v-col>
                </v-row>
                <v-row dense v-if="selectedBusiness">
                  <v-col cols="6" sm="6">
                    <v-switch
                      class="mt-0"
                      v-model="sessionInventoryFileUsage"
                      label="Use business inventory_items.csv"
                      :disabled="!recentSession || !sessionInventoryFilePath"
                      :loading="sessionsLoading || sessionSettingsChecking"
                    />
                  </v-col>
                </v-row>
                <template v-if="selectedInputFilesNames">
                  <v-row
                    v-for="selectedInputFilesName in [
                      ...selectedInputFilesNames,
                      ...inputFilesNames
                    ]"
                    :key="
                      selectedInputFilesName.id + selectedInputFilesName.name
                    "
                  >
                    <v-col cols="6">
                      <h3>
                        {{ selectedInputFilesName.name }}
                      </h3>
                    </v-col>
                    <v-col cols="6">
                      <v-combobox
                        label="Input File Type"
                        :items="
                          getFilesTypesByExtension(selectedInputFilesName.name)
                        "
                        :return-object="false"
                        v-model="selectedInputFilesName.type"
                        @change="onSelectInputFileType"
                      />
                    </v-col>
                  </v-row>
                </template>

                <h3>Sales history</h3>
                <v-divider horizontal></v-divider>
                <v-row>
                  <v-col cols="5.5">
                    <UploadsSelect
                      label="Select file to process"
                      :uploads="salesHistories"
                      :selected="selectedHistoryFile"
                      @onSelect="(selected) => (selectedHistoryFile = selected)"
                    />
                  </v-col>
                  <v-col cols="1" align="center">
                    <h3>or</h3>
                  </v-col>
                  <v-col cols="5.5">
                    <v-file-input
                      accept=".csv, .xls, .xlsx"
                      show-size
                      v-model="historyFile"
                      label="Upload new file to process"
                    />
                  </v-col>
                </v-row>
                <h3>Previous run</h3>
                <v-divider horizontal></v-divider>
                <v-row>
                  <v-col cols="5.5">
                    <UploadsSelect
                      :disabled="addRecentResultId"
                      label="Select file to process"
                      :uploads="previousRuns"
                      :selected="selectedPreviousRunFile"
                      @onSelect="
                        (selected) => (selectedPreviousRunFile = selected)
                      "
                    />
                  </v-col>
                  <v-col cols="1" align="center">
                    <h3>or</h3>
                  </v-col>
                  <v-col cols="5.5">
                    <v-file-input
                      accept=".csv, .xls, .xlsx"
                      :disabled="addRecentResultId"
                      show-size
                      v-model="previousRunFile"
                      label="Upload new file to process"
                    />
                  </v-col>
                </v-row>
                <v-row dense v-if="recentResultId">
                  <v-col cols="6" sm="6">
                    <v-switch
                      class="mt-0"
                      v-model="addRecentResultId"
                      label="Use recent previousRun from the copied task"
                    />
                  </v-col>
                </v-row>

                <h3>Inventory file</h3>
                <v-divider horizontal />
                <v-row>
                  <v-col cols="5.5">
                    <UploadsSelect
                      label="Select file to process"
                      :uploads="inventoryFiles"
                      :selected="selectedInventoryFile"
                      @onSelect="
                        (selected) => (selectedInventoryFile = selected)
                      "
                    />
                  </v-col>
                  <v-col cols="1" align="center">
                    <h3>or</h3>
                  </v-col>
                  <v-col cols="5.5">
                    <v-file-input
                      accept=".csv"
                      show-size
                      v-model="inventoryFile"
                      label="Upload new file to process"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="5" class="mb-0 mt-0 pb-0 pt-0">
                <v-row>
                  <v-col cols="6" sm="6">
                    <v-switch
                      v-model="unitSystemCheck"
                      :label="`Units: ${unitSystemCheck ? 'imperial' : 'metric'}`"
                    />
                  </v-col>
                  <v-col cols="6" sm="6">
                    <v-switch
                      v-model="labMatchingTechniqueCheck"
                      :label="`Lab Matching Technique: ${labMatchingTechniqueCheck ? 'firm' : 'fuzzy'}`"
                    />
                  </v-col>
                </v-row>
                <CommonSelect
                  label="Category Filters"
                  multiple
                  :options="options.filterTypes"
                  :selectedOptions="selectedFilters"
                  :selectAll="true"
                  @change="onCategoryFilterChange"
                />
                <v-combobox
                  :items="options.labs"
                  v-model="defaultlab"
                  label="Default Labs"
                  multiple
                />
                <v-row>
                  <v-col cols="6" sm="6">
                    <v-combobox
                      :items="options.labs"
                      v-model="defaultReferenceLab"
                      label="Default Reference Lab"
                      multiple
                    />
                  </v-col>
                  <v-col cols="6" sm="6">
                    <v-combobox
                      :items="options.labs"
                      v-model="historicLab"
                      label="Historic Lab"
                      multiple
                    />
                  </v-col>
                </v-row>
                <v-col cols="12" class="mb-0 mt-0 pb-0 pt-0">
                  <v-switch
                    v-model="debug"
                    :label="`Debug: ${debug.toString()}`"
                  />
                </v-col>
                <v-col class="ma-0 pt-0" cols="12" sm="12">
                  <TaskParametersForm
                    :business="selectedBusiness"
                    :context="taskProps.params.context"
                    ref="taskParametersForm"
                  />
                </v-col>
              </v-col>
            </v-row>
            <small>*indicates required field</small>
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn color="success" text @click="openTaskContextDialog">
            Watch "Context"
            <v-icon dark right>mdi-wrench</v-icon>
          </v-btn>
          <v-spacer />
          <v-btn color="blue darken-1" text @click="dialog = false">
            Close
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :loading="loading"
            :disabled="sessionsLoading || sessionSettingsChecking"
            @click="create"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <TaskContextDialog ref="taskContextDialog" />
    <ImportMultiSiteConfigDialog ref="importMultiSiteConfigDialog" />
    <ImportSiteConfigDialog ref="importSiteConfigDialog" />
  </v-row>
</template>

<script>
import * as _ from 'lodash';
import { Axios } from '@/services';
import UploadsSelect from '../selects/UploadsSelect';
import TaskParametersForm from './TaskParametersForm';
import TaskContextDialog from '@/components/tasks/TaskContextDialog';
import PracticeSearch from '@/components/common/PracticeSearch';
import {
  DEFAULTS,
  MatcherCategoryFiltersToLabels,
  MatcherInputFileTypes,
  MatcherCategoryFilters
} from '@/constants';
import {
  SessionsTypesMap,
  InventoryItemsAvailableStatusesList
} from '@/constants';
import CONFIG from '@/config';
import { tasksApiV1, businessApiV1, filesApiV1 } from '@/api/v1';
import { getFilesTypes } from '@/helpers';
import ImportMultiSiteConfigDialog from '@/components/dialogs/ImportMultiSiteConfigDialog';
import ImportSiteConfigDialog from '@/components/dialogs/ImportSiteConfigDialog';
import CommonSelect from '../common/CommonSelect';

const PREPROCESSOR_GCS_OUTPUT_DIRECTORY = 'universal';
const AVIMARK_PIMS_ID = 'PL1J8Oa3';

export default {
  name: 'TaskCreationDialog',
  data: () => ({
    dialog: false,
    loading: false,
    isLoading: false,
    options: {
      labs: DEFAULTS.MATCHER.LABS,
      filterTypes: MatcherCategoryFiltersToLabels
    },
    uploads: [],
    salesHistories: [],
    previousRuns: [],
    inventoryFiles: [],
    selectedBusiness: undefined,
    selectedFilters: MatcherCategoryFiltersToLabels.map(({ value }) => value),
    unitSystemCheck: true,
    labMatchingTechniqueCheck: true,
    inputFiles: null,
    historyFile: null,
    previousRunFile: null,
    inventoryFile: null,
    selectedInputFiles: null,
    selectedHistoryFile: null,
    selectedPreviousRunFile: null,
    selectedInventoryFile: null,
    selectedInputFilesNames: [],
    inputFilesNames: [],
    debug: false,
    context: JSON.stringify(DEFAULTS.MATCHER.CONTEXT, undefined, 4),
    type: 'common',
    labMatchingTechnique: 'firm',
    defaultlab: ['idexx'],
    defaultReferenceLab: ['idexx'],
    historicLab: ['idexx', 'abaxis'],
    valid: true,
    name: '',
    nameRules: [
      (v) => !!v || 'Name is required',
      (v) => (v && v.length <= 120) || 'Name must be less than 120 characters'
    ],
    defaultTaskProps: { params: { context: undefined } },
    taskProps: { params: { context: undefined } },
    taskId: '',
    recentResultId: '',
    addRecentResultId: false,
    selectedEnv: CONFIG.ENV,
    sessionsLoading: false,
    sessionSettingsChecking: false,
    recentSession: null,
    sessionInventoryFileUsage: false,
    sessionInventoryFilePath: null,
    shouldStartPreprocessor: false
  }),
  watch: {
    selectedInputFiles(newValue) {
      let intermediateArray = [];

      (newValue || []).forEach((file) => {
        const previousFile = this.selectedInputFilesNames.find(
          (prevFile) => prevFile.id === file.id && prevFile.name === file.name
        );

        intermediateArray.push({
          id: file.id,
          name: file.name,
          type: previousFile && previousFile.type ? previousFile.type : 'common'
        });
      });

      this.selectedInputFilesNames = intermediateArray;
    },
    inputFiles(newValue) {
      let intermediateArray = [];

      (newValue || []).forEach((file) => {
        const previousFile = this.inputFilesNames.find(
          (prevFile) => prevFile.id === file.id && prevFile.name === file.name
        );
        const isCsvFile = this.isCsvFile(file.name);

        intermediateArray.push({
          name: file.name,
          type:
            previousFile && previousFile.type
              ? previousFile.type
              : isCsvFile
                ? 'common'
                : 'cornerstoneprice'
        });
      });

      this.inputFilesNames = intermediateArray;
    },
    async recentSession(newValue) {
      if (newValue) {
        const { id, status, importSessionGroupId, sourcePimsId } = newValue;

        if (InventoryItemsAvailableStatusesList.includes(status)) {
          this.sessionsLoading = true;
          await this.fetchSessionSourceDataLocation();
        }

        if (importSessionGroupId) {
          this.sessionSettingsChecking = true;
          const isAvimarkPims = sourcePimsId === AVIMARK_PIMS_ID;
          const { preprocessorMultiClinicSite, ...rest } =
            await businessApiV1.fetchSessionSettings(this.selectedEnv, id);

          if (!isAvimarkPims && !preprocessorMultiClinicSite) {
            this.$refs.importSiteConfigDialog.open(this.selectedEnv, id);
          }
          if (isAvimarkPims) {
            this.$refs.importMultiSiteConfigDialog.open(this.selectedEnv, id, {
              preprocessorMultiClinicSite,
              ...rest
            });
          }

          this.shouldStartPreprocessor = true;
          this.sessionSettingsChecking = false;
        }
      }
    }
  },
  components: {
    UploadsSelect,
    TaskParametersForm,
    TaskContextDialog,
    PracticeSearch,
    ImportSiteConfigDialog,
    ImportMultiSiteConfigDialog,
    CommonSelect
  },
  created() {
    this.getUploads();
    this.getSalesHistory();
    this.getPreviousRuns();
    this.getInventoryFiles();
  },
  methods: {
    isCsvFile(fileName) {
      const extension = fileName.split('.').pop();

      return extension === 'csv';
    },
    getFilesTypesByExtension(fileName) {
      const isCsv = this.isCsvFile(fileName);
      const disabledTypesList = isCsv ? [] : ['common'];

      return getFilesTypes(disabledTypesList);
    },
    getUploads() {
      filesApiV1
        .getFiles({
          page: 1,
          rowsPerPage: 100,
          type: 'uploads'
        })
        .then((res) => {
          this.uploads = res.items;
        });
    },
    getSalesHistory() {
      filesApiV1
        .getFiles({
          page: 1,
          rowsPerPage: 100,
          type: 'sales_histories'
        })
        .then((res) => {
          this.salesHistories = res.items;
        });
    },
    getPreviousRuns() {
      filesApiV1
        .getFiles({
          page: 1,
          rowsPerPage: 100,
          type: 'previous_runs'
        })
        .then((res) => {
          this.previousRuns = res.items;
        });
    },
    getInventoryFiles() {
      filesApiV1
        .getFiles({
          page: 1,
          rowsPerPage: 100,
          type: 'inventory_files'
        })
        .then((res) => {
          this.inventoryFiles = res.items;
        });
    },
    open(taskProps = this.defaultTaskProps) {
      this.taskProps = taskProps;
      this.parseCopiedProps(taskProps);
      this.dialog = true;
      this.taskId = taskProps.id;
      this.recentResultId = taskProps.result_id;
    },
    close() {
      this.selectedInputFiles = undefined;
      this.taskProps = this.defaultTaskProps;
      this.dialog = false;
    },
    parseCopiedProps(task) {
      if (task?.params?.context) {
        const {
          uploads,
          history,
          previousRun,
          inventoryFile,
          params: {
            defaultReferenceLab,
            defaultlab,
            historicLab,
            labMatchingTechnique,
            unitSystem,
            inputFilesParams,
            filters
          }
        } = task;

        if (uploads && uploads.length) {
          this.selectedInputFilesNames = inputFilesParams;
          this.selectedInputFiles = uploads;
        } else this.selectedInputFiles = null;

        this.selectedHistoryFile = history || null;
        this.selectedPreviousRunFile = previousRun || null;
        this.selectedInventoryFile = inventoryFile || null;

        this.defaultlab = defaultlab || ['idexx'];
        this.defaultReferenceLab = defaultReferenceLab || ['idexx'];
        this.historicLab = historicLab || ['idexx', 'abaxis'];
        this.labMatchingTechniqueCheck = labMatchingTechnique === 'firm';

        this.unitSystemCheck = unitSystem === 'imperial';
        this.selectedFilters = filters;
      }
    },
    create() {
      if (!this.$refs.form.validate()) return;

      const formData = new FormData();
      const contextObject = this.$refs.taskParametersForm.getData();

      if (this.sessionInventoryFileUsage && this.sessionInventoryFilePath) {
        formData.append(
          'inventoryItemsFilePath',
          this.sessionInventoryFilePath
        );
        formData.append('reSessionId', this.recentSession.id);
        this.selectedInputFilesNames = [];
        this.inputFilesNames = [
          {
            name: 'inventory_items.csv',
            type: 'common'
          }
        ];
      }

      if (this.shouldStartPreprocessor) {
        formData.append(
          'shouldStartPreprocessor',
          this.shouldStartPreprocessor
        );
      }

      formData.append('environment', this.selectedEnv);
      formData.append('name', this.name);
      formData.append('business', JSON.stringify(this.selectedBusiness));
      formData.append(
        'inputFilesParams',
        JSON.stringify([
          ...this.selectedInputFilesNames,
          ...this.inputFilesNames
        ])
      );

      formData.append(
        'params',
        JSON.stringify({
          unitSystem: this.unitSystemCheck ? 'imperial' : 'metric',
          debug: this.debug,
          context: JSON.stringify(contextObject),
          type: this.type,
          labMatchingTechnique: this.labMatchingTechniqueCheck
            ? 'firm'
            : 'fuzzy',
          defaultlab: this.defaultlab,
          defaultReferenceLab: this.defaultReferenceLab,
          historicLab: this.historicLab,
          filters: this.selectedFilters,
          ...(this.recentResultId && this.addRecentResultId
            ? { recentResultId: parseInt(this.recentResultId, 10) }
            : {})
        })
      );

      this.__setFiles(formData);

      this.loading = true;
      // New task dialog is used for V2 Matcher Tasks
      tasksApiV1
        .createTask(formData)
        .then(() => {
          this.loading = false;
          this.close();
        })
        .catch((err) => {
          console.log('Err: ', err);
          this.loading = false;
        });
    },
    __setFiles(formData) {
      const filesArray = [];
      //setInputFile

      if (
        !this.sessionInventoryFileUsage &&
        this.selectedInputFiles &&
        this.selectedInputFiles.length
      ) {
        this.selectedInputFiles.forEach((file) =>
          filesArray.push({
            type: file.file_type,
            id: file.id
          })
        );
      }
      if (this.selectedHistoryFile && this.selectedHistoryFile.id) {
        filesArray.push({
          type: this.selectedHistoryFile.file_type,
          id: this.selectedHistoryFile.id
        });
      }
      if (
        !this.addRecentResultId &&
        this.selectedPreviousRunFile &&
        this.selectedPreviousRunFile.id
      ) {
        filesArray.push({
          type: this.selectedPreviousRunFile.file_type,
          id: this.selectedPreviousRunFile.id
        });
      }
      if (this.selectedInventoryFile && this.selectedInventoryFile.id) {
        filesArray.push({
          type: this.selectedInventoryFile.file_type,
          id: this.selectedInventoryFile.id
        });
      }

      if (
        !this.sessionInventoryFileUsage &&
        this.inputFiles &&
        this.inputFiles.length
      ) {
        this.inputFiles.forEach((file, index) =>
          formData.append(`uploads_${index}`, file)
        );
      }
      if (this.historyFile) {
        formData.append(`sales_histories_0`, this.historyFile);
      }
      if (!this.addRecentResultId && this.previousRunFile) {
        formData.append(`previous_runs_0`, this.previousRunFile);
      }
      if (this.inventoryFile) {
        formData.append(`inventory_files_0`, this.inventoryFile);
      }

      if (filesArray && filesArray.length) {
        formData.append('files', JSON.stringify(filesArray));
      }
    },
    openTaskContextDialog() {
      const contextObject = this.$refs.taskParametersForm.getData();

      this.$refs.taskContextDialog.open(contextObject);
    },
    async handleBusinessChange(business) {
      this.selectedBusiness = business;
      const sessions = await this.getBusinessSessions(business.id);
      this.getBusinessRecentSession(sessions);
    },
    handleEnvChanged(env) {
      this.selectedEnv = env;
    },
    async getBusinessSessions(businessId) {
      this.sessionsLoading = true;
      const sessions = await businessApiV1.fetchBusinessSessions(
        businessId,
        this.selectedEnv
      );
      this.sessionsLoading = false;

      return sessions;
    },
    getBusinessRecentSession(sessions) {
      const reSessions = _.filter(sessions, {
        active: true,
        type: SessionsTypesMap.RHAPSODY_ENTERPRISE
      });
      this.recentSession = _.last(_.sortBy(reSessions, 'date'));
    },
    async fetchSessionSourceDataLocation() {
      const { id } = this.recentSession;

      const { sourceDataLocation, destinationDataLocation } =
        await businessApiV1.fetchBusinessSessionSourceDataLocation(
          id,
          this.selectedEnv
        );

      if (destinationDataLocation || sourceDataLocation) {
        this.sessionInventoryFilePath =
          destinationDataLocation ||
          `${sourceDataLocation}/${PREPROCESSOR_GCS_OUTPUT_DIRECTORY}/`;
        this.sessionInventoryFileUsage = true;
      }

      this.sessionsLoading = false;
    },

    /**
     * The localfood catalog has a default supported category of food. Since this is the only one that
     * does, it's fine to hardcode the behavior here for now. If there ends up being more that need specific
     * filtering behavior, this can be hoisted into some constants file.
     */
    onSelectInputFileType() {
      const selectedFiles = [
        ...this.selectedInputFilesNames,
        ...this.inputFilesNames
      ];
      const isLocalFoodMatching = selectedFiles.some(
        ({ type }) => type === MatcherInputFileTypes.LOCALFOODCATALOG
      );
      if (isLocalFoodMatching) {
        this.selectedFilters = [MatcherCategoryFilters.FOOD];
      }
    },

    /**
     * Updates the selectedFilters array
     * @param {Array<string>} selectedFilters the current array of filter values
     */
    onCategoryFilterChange(selectedFilters) {
      this.selectedFilters = selectedFilters;
    }
  }
};
</script>
